import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

const VOTE_CHIP_CLASS = {
  PREFERRED:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  FEASIBLE:
    "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
};

function rowKey(row) {
  return `${row.pollId}::${row.slotId}`;
}

export function PropagateVotesPromptDialog({
  open,
  candidates,
  contextLabel,
  onApply,
  onSkip,
  applying = false,
}) {
  const candidateList = useMemo(
    () => (Array.isArray(candidates) ? candidates : []),
    [candidates],
  );

  // Default-checked state. The component is mounted fresh each time the
  // parent decides to open it (parent guards with `{open && <Dialog .../>}`),
  // so this initializer runs on every open and we don't need a useEffect
  // to reset state when `open` flips.
  const [checked, setChecked] = useState(() => {
    const init = {};
    for (const row of candidateList) init[rowKey(row)] = true;
    return init;
  });

  const grouped = useMemo(() => {
    const m = new Map();
    for (const row of candidateList) {
      if (!m.has(row.pollId)) {
        m.set(row.pollId, { pollTitle: row.pollTitle, rows: [] });
      }
      m.get(row.pollId).rows.push(row);
    }
    return [...m.entries()];
  }, [candidateList]);

  const totalChecked = Object.values(checked).filter(Boolean).length;

  function toggle(key) {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleApply() {
    const selected = candidateList.filter((row) => checked[rowKey(row)]);
    onApply(selected);
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onSkip?.()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Restore your votes?</DialogTitle>
          <DialogDescription>
            You changed your RSVP on{" "}
            <strong>{contextLabel || "another session"}</strong>. The slots
            below were cleared because of that booking. Want to put your
            previous votes back?
          </DialogDescription>
        </DialogHeader>

        {candidateList.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">
            Nothing to restore.
          </p>
        ) : (
          <div className="max-h-96 space-y-4 overflow-y-auto">
            {grouped.map(([pollId, { pollTitle, rows }]) => (
              <section key={pollId}>
                <h4 className="font-semibold text-slate-700 dark:text-slate-200">
                  {pollTitle}
                </h4>
                <ul className="mt-2 space-y-1">
                  {rows.map((row) => {
                    const key = rowKey(row);
                    return (
                      <li
                        key={key}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          aria-label={`Restore vote for ${row.slotLabel}`}
                          checked={!!checked[key]}
                          onChange={() => toggle(key)}
                        />
                        <span>{row.slotLabel}</span>
                        <span
                          className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${
                            VOTE_CHIP_CLASS[row.suggestedVote] ||
                            "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {row.suggestedVote}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <button
            type="button"
            onClick={onSkip}
            className="rounded-xl border px-4 py-2 text-sm"
          >
            Not now
          </button>
          <button
            type="button"
            disabled={applying || totalChecked === 0}
            onClick={handleApply}
            className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary/90 disabled:opacity-50"
          >
            {applying
              ? "Applying…"
              : `Restore ${totalChecked} vote${totalChecked === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
